/* eslint-disable no-unused-vars */
import { X } from 'lucide-react';
import React from 'react';
import {
  createRuntimeConfig,
  createRuntimeConfigEntry,
  deleteRuntimeConfigEntry,
  updateRuntimeConfig,
  updateRuntimeConfigEntry
} from '../../api';
import { useFeedback } from '../../contexts/FeedbackContext';
import { useSession } from '../../contexts/SessionContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import type { UseActionDialogReturn } from '../../hooks/useActionDialog';
import { FieldTooltip } from '../FieldTooltip';
import { formatReleaseAppVersionLabel } from '../../utils/formatters';
import { MANAGED_RUNTIME_CONFIG_VALUE_KEYS } from '../../utils/runtimeConfigValueValidation';
import { buildRuntimeConfigValueRows } from '../../utils/runtimeConfigDialogModel';
import { RuntimeConfigValueListEditor } from './RuntimeConfigValueListEditor';

type RuntimeConfigDialogProps = {
  actionDialogReturn: UseActionDialogReturn;
  onNavigate: (path: string, opts?: { replace?: boolean }) => void;
};

function RuntimeConfigDialog({
  actionDialogReturn,
  onNavigate: _onNavigate
}: RuntimeConfigDialogProps) {
  const {
    actionDialog,
    dialogBusy,
    dialogError,
    setActionDialog,
    setDialogBusy,
    setDialogError
  } = actionDialogReturn;

  const { session } = useSession();
  const { toast } = useFeedback();
  const {
    workspace,
    activeOrganization,
    activeProject,
    releaseAppVersionById,
    releaseAppById,
    channels,
    visibleEnvironments,
    setWorkspaceVersion
  } = useWorkspace();

  const { environments, releaseAppVersions } = workspace;

  // Entry ids removed from actionDialog.values during this edit session —
  // deleted from the server only on submit, alongside the rule save. Reset
  // whenever a different rule (or a fresh "create") is opened — this
  // component stays mounted across dialog open/close, so state would
  // otherwise leak stale deletions from a previously edited rule into the
  // next one, which would silently no-op against the wrong rule's entries.
  const [pendingDeletions, setPendingDeletions] = React.useState<string[]>([]);
  const actionDialogKind = actionDialog?.kind;
  const openRuntimeConfigId =
    actionDialog?.kind === 'runtime-config'
      ? actionDialog.runtimeConfigId
      : undefined;
  React.useEffect(() => {
    setPendingDeletions([]);
  }, [actionDialogKind, openRuntimeConfigId]);

  if (!actionDialog || actionDialog.kind !== 'runtime-config') return null;

  const close = () => {
    if (dialogBusy) return;
    setActionDialog(null);
    setPendingDeletions([]);
  };

  // Every opener should pass `values`, but a missing array must never crash
  // the whole admin UI — treat it as "no extra values yet".
  const dialogValues = actionDialog.values ?? [];
  const selectedDialogVersion = releaseAppVersionById.get(
    actionDialog.releaseAppVersionId
  );
  const compatibleReleaseVersionOptions = isNativePlatformValue(
    actionDialog.platform
  )
    ? releaseAppVersions.filter(
        (version) =>
          releaseAppById.get(version.releaseAppId)?.platform ===
          actionDialog.platform
      )
    : [];
  const derivedOtaChannel = selectedDialogVersion
    ? channels.find(
        (ch) =>
          ch.environment === actionDialog.environment &&
          ch.releaseAppVersionId === selectedDialogVersion.releaseAppVersionId
      )
    : undefined;
  const isNativePlatform =
    actionDialog.platform === 'ios' || actionDialog.platform === 'android';

  // Blank rows disappear on save. Any row with a key or value must pass validation.
  const { customRows, filledValueRows, hasInvalidValueRows } =
    buildRuntimeConfigValueRows(actionDialog);

  const canSubmit =
    actionDialog.name.trim().length >= 2 &&
    Boolean(activeOrganization?.id) &&
    Boolean(activeProject?.id) &&
    actionDialog.expressApiHostname.trim().length > 0 &&
    (!isNativePlatform || actionDialog.releaseAppVersionId.trim().length > 0) &&
    (!isNativePlatform || Boolean(derivedOtaChannel)) &&
    !hasInvalidValueRows;

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (
      !session ||
      !actionDialog ||
      actionDialog.kind !== 'runtime-config' ||
      dialogBusy
    )
      return;
    setDialogBusy(true);
    setDialogError('');
    try {
      const name = actionDialog.name.trim();
      const expressApiHostname = actionDialog.expressApiHostname.trim();
      if (name.length < 2) {
        setDialogError(
          'Release config name must be at least 2 characters long.'
        );
        return;
      }
      if (!expressApiHostname) {
        setDialogError('Release config needs an express API hostname.');
        return;
      }
      const isNative =
        actionDialog.platform === 'ios' || actionDialog.platform === 'android';
      const submitVersion = isNative
        ? releaseAppVersionById.get(actionDialog.releaseAppVersionId.trim())
        : undefined;
      const submitOtaChannel = submitVersion
        ? channels.find(
            (ch) =>
              ch.environment === actionDialog.environment &&
              ch.releaseAppVersionId === submitVersion.releaseAppVersionId
          )
        : undefined;
      const submitEnv = environments.find(
        (env) => env.slug === actionDialog.environment
      );
      const payload = {
        name,
        organizationId: activeOrganization?.id,
        projectId: activeProject?.id,
        releaseAppVersionId: isNative
          ? actionDialog.releaseAppVersionId.trim() || null
          : undefined,
        tenantEnvironmentId: submitEnv?.tenantEnvironmentId || undefined,
        hostname: !isNative ? actionDialog.hostname.trim() || null : null,
        nativeAppId: isNative ? actionDialog.nativeAppId.trim() || null : null,
        platform: isNative ? actionDialog.platform : undefined,
        nativeVersionRange: isNative
          ? actionDialog.nativeVersionRange.trim() || null
          : null,
        environment: actionDialog.environment,
        expressApiHostname,
        staticCdnPrefix: actionDialog.staticCdnPrefix.trim() || null,
        otaChannelId: isNative
          ? submitOtaChannel?.releaseChannelId || null
          : null,
        otaChannel:
          isNative && submitOtaChannel
            ? `${submitOtaChannel.environment}/${submitOtaChannel.channel}`
            : null,
        analyticsEnabled: actionDialog.analyticsEnabled,
        analyticsPageviewsEnabled: actionDialog.analyticsPageviewsEnabled,
        posthogApiKey: actionDialog.posthogApiKey.trim() || null,
        posthogHost: actionDialog.posthogHost.trim() || null,
        otelEnabled: actionDialog.otelEnabled,
        otelCollectorUrl: actionDialog.otelCollectorUrl.trim() || null,
        priority: (() => {
          const parsedPriority = Number(actionDialog.priority.trim());
          return Number.isFinite(parsedPriority) ? parsedPriority : undefined;
        })(),
        isActive: actionDialog.isActive
      };
      let runtimeConfigId = actionDialog.runtimeConfigId;
      if (runtimeConfigId) {
        await updateRuntimeConfig(
          session.accessToken,
          runtimeConfigId,
          payload
        );
      } else {
        const created = await createRuntimeConfig(session.accessToken, payload);
        runtimeConfigId = created.runtimeConfigId;
      }

      for (const entryId of pendingDeletions) {
        await deleteRuntimeConfigEntry(
          session.accessToken,
          runtimeConfigId,
          entryId
        );
      }

      for (const row of filledValueRows) {
        const key = row.key.trim();
        if (row.runtimeConfigValueId) {
          await updateRuntimeConfigEntry(
            session.accessToken,
            runtimeConfigId,
            row.runtimeConfigValueId,
            {
              key,
              valueType: row.valueType,
              masked: row.masked,
              // Masked rows never get their plaintext pre-filled — if the
              // admin didn't explicitly retype it, leave the stored value
              // alone rather than overwriting it with an empty string.
              ...(row.valueTouched ? { value: row.value } : {})
            }
          );
        } else {
          await createRuntimeConfigEntry(session.accessToken, runtimeConfigId, {
            key,
            value: row.value,
            valueType: row.valueType,
            masked: row.masked
          });
        }
      }

      setWorkspaceVersion((v) => v + 1);
      toast(
        'success',
        actionDialog.runtimeConfigId
          ? 'Runtime rule updated'
          : 'Runtime rule created'
      );
      setActionDialog(null);
    } catch (err: any) {
      setDialogError(
        err?.response?.data?.message ||
          err?.message ||
          'Something went wrong. Please try again.'
      );
    } finally {
      setDialogBusy(false);
    }
  }

  return (
    <div className="dialogOverlay" role="presentation" onClick={close}>
      <form
        className="dialogPanel runtimeRuleDialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="runtime-config-dialog-title"
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
      >
        <div className="dialogHeader">
          <div>
            <div className="panelKicker">Client resolution</div>
            <h3 id="runtime-config-dialog-title">
              {actionDialog.runtimeConfigId
                ? 'Edit runtime rule'
                : 'Create runtime rule'}
            </h3>
            <p>
              Choose which clients this rule applies to and what settings they
              receive.
            </p>
          </div>
          <button
            type="button"
            className="iconButton"
            onClick={close}
            aria-label="Close dialog"
          >
            <X size={16} />
          </button>
        </div>
        <div className="settingsForm runtimeRuleForm">
          <div className="settingsGroup runtimeEditorGrid runtimeMatchGroup">
            <div className="settingsGroupLabel">1. Match clients</div>
            <label className="settingsField runtimeFieldWide">
              <span>Name</span>
              <input
                value={actionDialog.name}
                onChange={(e) =>
                  setActionDialog({ ...actionDialog, name: e.target.value })
                }
                placeholder="iOS Dev"
                autoFocus
              />
              <p className="fieldHint">
                Use a name that clearly maps to the platform and environment.
              </p>
            </label>
            <label className="settingsField">
              <span>Runtime environment</span>
              <select
                value={actionDialog.environment}
                onChange={(e) =>
                  setActionDialog({
                    ...actionDialog,
                    environment: e.target.value as
                      | 'dev'
                      | 'staging'
                      | 'production'
                      | 'internal'
                  })
                }
              >
                {visibleEnvironments.map((environment) => (
                  <option
                    key={environment.tenantEnvironmentId}
                    value={environment.slug}
                  >
                    {environment.name}
                  </option>
                ))}
              </select>
              {visibleEnvironments.length === 0 ? (
                <p className="fieldHint">
                  Show an environment from the Environments page before creating
                  a rule.
                </p>
              ) : null}
            </label>
            <label className="settingsField">
              <span>Platform</span>
              <select
                value={actionDialog.platform}
                onChange={(e) => {
                  const platform = e.target.value as 'web' | 'android' | 'ios';
                  const currentVersion = releaseAppVersionById.get(
                    actionDialog.releaseAppVersionId
                  );
                  const currentVersionPlatform = currentVersion
                    ? releaseAppById.get(currentVersion.releaseAppId)?.platform
                    : undefined;
                  setActionDialog({
                    ...actionDialog,
                    platform,
                    releaseAppVersionId:
                      platform === currentVersionPlatform
                        ? actionDialog.releaseAppVersionId
                        : ''
                  });
                }}
              >
                <option value="web">Web</option>
                <option value="ios">iOS</option>
                <option value="android">Android</option>
              </select>
            </label>
            {actionDialog.platform === 'web' && (
              <label className="settingsField">
                <span>
                  Hostname
                  <FieldTooltip text="Pin this config to a specific origin hostname (e.g. app.example.com). Leave blank to match any host — useful as a catch-all fallback." />
                </span>
                <input
                  value={actionDialog.hostname}
                  onChange={(e) =>
                    setActionDialog({
                      ...actionDialog,
                      hostname: e.target.value
                    })
                  }
                  placeholder="app.example.com"
                />
              </label>
            )}
            <label className="settingsField">
              <span>
                Priority
                <FieldTooltip text="When multiple configs match a request, the one with the highest priority number wins. Use 0 for catch-all configs and higher values for more specific ones." />
              </span>
              <input
                type="number"
                min="0"
                max="100"
                value={actionDialog.priority}
                onChange={(e) =>
                  setActionDialog({ ...actionDialog, priority: e.target.value })
                }
              />
            </label>
            <div className="settingsField runtimeToggleField">
              <span>
                Active
                <FieldTooltip text="Inactive configs are stored but never served to clients. Use this to draft a config before going live." />
              </span>
              <label className="runtimeToggleRow">
                <div>
                  <strong>Enabled</strong>
                  <span>Serve this rule to matching clients.</span>
                </div>
                <input
                  type="checkbox"
                  checked={actionDialog.isActive}
                  onChange={(e) =>
                    setActionDialog({
                      ...actionDialog,
                      isActive: e.target.checked
                    })
                  }
                />
              </label>
            </div>
          </div>
          <div className="settingsGroup runtimeEditorGrid runtimeDeliveryGroup">
            <div className="settingsGroupLabel">2. App endpoints</div>
            <label className="settingsField">
              <span>
                Express API hostname
                <FieldTooltip text="The base URL of your backend API. Clients read this from the config at startup and use it for all API calls. Required." />
              </span>
              <input
                value={actionDialog.expressApiHostname}
                onChange={(e) =>
                  setActionDialog({
                    ...actionDialog,
                    expressApiHostname: e.target.value
                  })
                }
                placeholder="https://api.example.com"
              />
            </label>
            <label className="settingsField">
              <span>
                Static CDN prefix
                <FieldTooltip text="Base URL for serving static assets (images, fonts, etc). Clients prepend this to relative asset paths. Leave blank to use the API hostname." />
              </span>
              <input
                value={actionDialog.staticCdnPrefix}
                onChange={(e) =>
                  setActionDialog({
                    ...actionDialog,
                    staticCdnPrefix: e.target.value
                  })
                }
                placeholder="https://cdn.example.com"
              />
            </label>
          </div>
          {(actionDialog.platform === 'ios' ||
            actionDialog.platform === 'android') && (
            <div className="settingsGroup runtimeEditorGrid runtimeOtaGroup">
              <div className="settingsGroupLabel">3. OTA target</div>
              <label className="settingsField">
                <span>
                  App version
                  <FieldTooltip text="Lock OTA bundle delivery to a specific app store version track. Devices on a different native version will not receive updates from this config." />
                </span>
                <select
                  value={actionDialog.releaseAppVersionId}
                  onChange={(e) =>
                    setActionDialog({
                      ...actionDialog,
                      releaseAppVersionId: e.target.value
                    })
                  }
                >
                  <option value="">
                    {compatibleReleaseVersionOptions.length > 0
                      ? `Choose an ${actionDialog.platform.toUpperCase()} app version`
                      : `No ${actionDialog.platform.toUpperCase()} versions yet`}
                  </option>
                  {compatibleReleaseVersionOptions.map((version) => (
                    <option
                      key={version.releaseAppVersionId}
                      value={version.releaseAppVersionId}
                    >
                      {formatReleaseAppVersionLabel(version, releaseAppById)}
                    </option>
                  ))}
                </select>
              </label>
              <div className="settingsField">
                <span>
                  OTA lane
                  <FieldTooltip text="Auto-derived from the selected app version and runtime environment. The lane is the delivery channel that serves the active bundle to devices." />
                </span>
                <div
                  style={{
                    padding: '6px 8px',
                    background: 'var(--surface)',
                    border: '1px solid var(--border)',
                    borderRadius: 6,
                    fontSize: 13,
                    color: derivedOtaChannel
                      ? 'var(--text)'
                      : 'var(--text-faint)'
                  }}
                >
                  {derivedOtaChannel
                    ? `${derivedOtaChannel.environment}/${derivedOtaChannel.channel}`
                    : selectedDialogVersion
                      ? `No lane found for ${actionDialog.environment}/${selectedDialogVersion.version} — create it first`
                      : 'Derived from app version + runtime environment'}
                </div>
              </div>
              <label className="settingsField">
                <span>
                  Native app ID
                  <FieldTooltip text="Bundle identifier (iOS) or package name (Android), e.g. com.example.app. Hard-pins this config to one specific native app binary." />
                </span>
                <input
                  value={actionDialog.nativeAppId}
                  onChange={(e) =>
                    setActionDialog({
                      ...actionDialog,
                      nativeAppId: e.target.value
                    })
                  }
                  placeholder="com.example.app"
                />
              </label>
              <label className="settingsField">
                <span>
                  Native version range
                  <FieldTooltip text="Semver range (e.g. >=1.2.0 <2.0.0) of native app builds that this config applies to. Leave blank to match all native versions." />
                </span>
                <input
                  value={actionDialog.nativeVersionRange}
                  onChange={(e) =>
                    setActionDialog({
                      ...actionDialog,
                      nativeVersionRange: e.target.value
                    })
                  }
                  placeholder=">=1.0.0"
                />
              </label>
            </div>
          )}
          <div className="settingsGroup runtimeAnalyticsGroup">
            <div className="settingsGroupLabel">Analytics delivery</div>
            <div className="settingsField">
              <div style={{ display: 'grid', gap: 8 }}>
                <label
                  className="onboardingChoice"
                  style={{ textAlign: 'left' }}
                >
                  <div className="onboardingChoiceTop">
                    <strong>
                      Enable analytics
                      <FieldTooltip text="Include analytics configuration in the runtime config payload. Clients use this to initialise their analytics provider." />
                    </strong>
                    <input
                      type="checkbox"
                      checked={actionDialog.analyticsEnabled}
                      onChange={(e) =>
                        setActionDialog({
                          ...actionDialog,
                          analyticsEnabled: e.target.checked
                        })
                      }
                    />
                  </div>
                  <span>Include analytics wiring in config payload.</span>
                </label>
                <label
                  className="onboardingChoice"
                  style={{ textAlign: 'left' }}
                >
                  <div className="onboardingChoiceTop">
                    <strong>
                      Track pageviews
                      <FieldTooltip text="Automatically fire a pageview event on every route change. Requires analytics to be enabled." />
                    </strong>
                    <input
                      type="checkbox"
                      checked={actionDialog.analyticsPageviewsEnabled}
                      onChange={(e) =>
                        setActionDialog({
                          ...actionDialog,
                          analyticsPageviewsEnabled: e.target.checked
                        })
                      }
                    />
                  </div>
                  <span>Automatic pageview events on route changes.</span>
                </label>
              </div>
            </div>
            {actionDialog.analyticsEnabled ? (
              <label className="settingsField">
                <span>
                  PostHog API key
                  <FieldTooltip text="Your PostHog project API key (starts with phc_). Only needed if you use PostHog as your analytics provider." />
                </span>
                <input
                  value={actionDialog.posthogApiKey}
                  onChange={(e) =>
                    setActionDialog({
                      ...actionDialog,
                      posthogApiKey: e.target.value
                    })
                  }
                  placeholder="phc_..."
                />
              </label>
            ) : null}
            {actionDialog.analyticsEnabled ? (
              <label className="settingsField">
                <span>
                  PostHog host
                  <FieldTooltip text="Custom PostHog ingestion endpoint. Use the EU cloud (eu.i.posthog.com) or your self-hosted instance. Defaults to app.posthog.com if blank." />
                </span>
                <input
                  value={actionDialog.posthogHost}
                  onChange={(e) =>
                    setActionDialog({
                      ...actionDialog,
                      posthogHost: e.target.value
                    })
                  }
                  placeholder="https://eu.i.posthog.com"
                />
              </label>
            ) : null}
          </div>
          <div className="settingsGroup runtimeObservabilityGroup">
            <div className="settingsGroupLabel">Observability delivery</div>
            <div className="settingsField">
              <label className="onboardingChoice" style={{ textAlign: 'left' }}>
                <div className="onboardingChoiceTop">
                  <strong>
                    OpenTelemetry
                    <FieldTooltip text="Include an OTEL collector URL in the config payload. Your SDK will forward traces and metrics to this endpoint." />
                  </strong>
                  <input
                    type="checkbox"
                    checked={actionDialog.otelEnabled}
                    onChange={(e) =>
                      setActionDialog({
                        ...actionDialog,
                        otelEnabled: e.target.checked
                      })
                    }
                  />
                </div>
                <span>Include OTEL collector settings in config payload.</span>
              </label>
            </div>
            {actionDialog.otelEnabled ? (
              <label className="settingsField">
                <span>
                  OTEL collector URL
                  <FieldTooltip text="HTTP endpoint of your OpenTelemetry collector (e.g. https://otel.example.com/v1/traces). Only used when OpenTelemetry is enabled." />
                </span>
                <input
                  value={actionDialog.otelCollectorUrl}
                  onChange={(e) =>
                    setActionDialog({
                      ...actionDialog,
                      otelCollectorUrl: e.target.value
                    })
                  }
                  placeholder="https://otel.example.com"
                />
              </label>
            ) : null}
          </div>
          <RuntimeConfigValueListEditor
            values={customRows}
            onChange={(nextValues) => {
              const removedIds = customRows
                .filter((row) => row.runtimeConfigValueId)
                .map((row) => row.runtimeConfigValueId as string)
                .filter(
                  (id) =>
                    !nextValues.some((row) => row.runtimeConfigValueId === id)
                );
              if (removedIds.length > 0) {
                setPendingDeletions((prev) => [...prev, ...removedIds]);
              }
              const retainedManagedRows = dialogValues.filter((row) =>
                MANAGED_RUNTIME_CONFIG_VALUE_KEYS.has(row.key)
              );
              setActionDialog({
                ...actionDialog,
                values: [...retainedManagedRows, ...nextValues]
              });
            }}
          />
        </div>
        {dialogError && <p className="fieldError">{dialogError}</p>}
        <div className="dialogActions">
          <button
            type="button"
            className="secondaryButton"
            onClick={close}
            disabled={dialogBusy}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="primaryButton"
            disabled={dialogBusy || !canSubmit}
          >
            {dialogBusy
              ? 'Saving…'
              : actionDialog.runtimeConfigId
                ? 'Save rule'
                : 'Create rule'}
          </button>
        </div>
      </form>
    </div>
  );
}

export { RuntimeConfigDialog };

function isNativePlatformValue(
  platform: 'web' | 'android' | 'ios'
): platform is 'android' | 'ios' {
  return platform === 'android' || platform === 'ios';
}
